import { useEffect, useState } from "react";
import { Bell, Bot, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export const GLASS = "backdrop-blur-xl bg-[#111827]/75 border border-[#00F0FF]/20 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.37)]";
export const CTA = "group relative flex items-center gap-3 overflow-hidden rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#00A3FF] px-6 py-3.5 font-bold tracking-wide text-slate-950 shadow-[0_0_25px_rgba(0,240,255,0.4)] transition-shadow duration-300 hover:shadow-[0_0_35px_rgba(0,240,255,0.7)]";
export const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const AGENT = { operativo: ["Operativo", "bg-emerald-400", "text-emerald-300"], inattivo: ["Inattivo", "bg-slate-400", "text-slate-300"], errore: ["Errore", "bg-red-500", "text-red-300"], manutenzione: ["Manutenzione", "bg-amber-400", "text-amber-300"] };
const CHECK_LABELS = { database: "Database", ai_llm: "AI", email_agente: "Email agente", email_transazionali: "Email", video_daily: "Video", pagamenti: "Pagamenti" };
const CHECK_TONE = { ok: "border-emerald-500/30 text-emerald-300", simulato: "border-amber-500/30 text-amber-300", limite_raggiunto: "border-amber-500/30 text-amber-300", non_configurato: "border-slate-500/30 text-slate-400", errore: "border-red-500/40 text-red-300" };

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="font-mono text-right" data-testid="sa-header-clock">
      <p className="text-2xl font-semibold text-white">{t.toLocaleTimeString("it-IT")}</p>
      <p className="text-[11px] uppercase tracking-widest text-slate-400">{t.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
    </div>
  );
}

export function useHealth() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => api.get("/superadmin/cc/health").then(({ data }) => setH(data)).catch(() => setH({ agent_status: "errore", checks: {} }));
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, []);
  return h;
}

export function CommandHeader({ health, onTalk }) {
  const [label, dot, txt] = AGENT[health?.agent_status] || ["Verifica…", "bg-slate-500", "text-slate-400"];
  return (
    <header className={`${GLASS} relative overflow-hidden p-5 sm:p-7`} data-testid="sa-header">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#00F0FF]/10 blur-3xl" />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#00F0FF]/80">Super Admin · accesso protetto MFA</p>
          <h1 data-testid="sa-header-title" className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">DOCTOR SELVIX <span className="text-slate-500">—</span> <span className="bg-gradient-to-r from-[#00F0FF] to-slate-200 bg-clip-text text-transparent">AI COMMAND CENTER</span></h1>
          <p className="mt-2 text-sm text-slate-400 sm:text-base">Il centro di controllo intelligente della tua clinica virtuale.</p>
        </div>
        <div className="flex items-start justify-between gap-4 lg:flex-col lg:items-end"><Clock />
          <div className="flex items-center gap-2">
            <span data-testid="sa-header-status-badge" className={`flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/70 px-3 py-1.5 font-mono text-xs ${txt}`}><span className={`h-2 w-2 rounded-full ${dot} motion-safe:animate-pulse`} /><Bot className="h-3.5 w-3.5" />Agente AI: {label}</span>
            <span data-testid="sa-header-notifications" className="flex items-center gap-1 rounded-full border border-white/10 bg-slate-900/70 px-3 py-1.5 font-mono text-xs text-slate-300"><Bell className="h-3.5 w-3.5" />{health?.notifications ?? "–"}</span>
          </div>
        </div>
      </div>
      <div className="relative mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="sa-services-status">
          {Object.entries(health?.checks || {}).map(([k, v]) => <span key={k} data-testid={`sa-service-${k}`} className={`rounded-full border bg-slate-900/60 px-2.5 py-1 font-mono text-[11px] ${CHECK_TONE[v] || CHECK_TONE.errore}`}>{CHECK_LABELS[k] || k}: {v.replace("_", " ")}</span>)}
        </div>
        <button data-testid="sa-talk-cta" onClick={onTalk} className={CTA}><Sparkles className="h-5 w-5" />PARLA CON DOCTOR SELVIX AI</button>
      </div>
    </header>
  );
}
